import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { PortalIcon, normalizePortalText } from 'utils/baochauTheme';

const Wrapper = styled.div`
  @keyframes loading {
    to {
      background-position-x: -30%;
    }
  }
  .loader__filter__searchbar {
    background-color: #ededed;
    height: 62px;
    border-radius: 7px;
    width: 100%;
  }
  .bw_frm_box .loader__filter__searchbar {
    background-color: #ededed;
    background: linear-gradient(
        100deg,
        rgba(255, 255, 255, 0) 40%,
        rgba(255, 255, 255, 0.5) 50%,
        rgba(255, 255, 255, 0) 60%
      )
      #ededed;
    background-size: 200% 100%;
    background-position-x: 180%;
    animation: 1s loading ease-in-out infinite;
  }

  &&.bw_search_box_compact {
    margin: 0;
    padding: 10px 12px 12px;
    border: 1px solid var(--portal-border-color, #e4e7ec);
    border-radius: var(--portal-radius-card, 10px);
    background: var(--bc-color-bg-soft, #f8fafc);

    .bw_title_search {
      display: flex;
      align-items: center;
      min-height: 20px;
      margin: 0 0 8px;
      padding: 0;
      color: var(--bc-color-text-strong, #101828);
      font-size: 14px;
      line-height: 20px;
    }

    .bw_title_search > span:first-child {
      font-weight: 700;
    }

    .bw_compact_advanced_toggle {
      margin-left: 12px;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--bc-color-text-muted, #667085);
      font-size: 12px;
      line-height: 20px;
      cursor: pointer;
    }

    .bw_compact_advanced_toggle:hover {
      color: var(--bc-color-brand, #ed3237);
    }

    .bw_close_search {
      margin-left: auto;
    }

    .bw_search_compact_grid {
      display: grid;
      grid-template-columns: repeat(var(--bw-filter-columns, 4), minmax(132px, 1fr)) auto;
      align-items: end;
      gap: 8px;
      margin: 0;
    }

    .bw_search_compact_grid > [class*='bw_col_'] {
      width: auto;
      max-width: none;
      padding: 0;
    }

    .bw_frm_box {
      min-height: 0;
      margin: 0;
      padding: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }

    .bw_frm_box > span:first-child:not(:last-child),
    .bw_frm_box > label,
    .bw_frm_box .bw_label {
      display: block;
      min-height: 17px;
      margin: 0 0 3px;
      color: var(--bc-color-text-muted-strong, #475467);
      font-size: 12px;
      font-weight: 600;
      line-height: 17px;
    }

    .loader__filter__searchbar {
      height: 54px;
    }

    .bw_search_compact_actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      min-width: 74px;
      padding-bottom: 0;
    }

    .bw_search_compact_actions .bw_btn_icon_only {
      width: 34px;
      min-width: 34px;
      margin: 0;
      padding: 0;
    }
  }

  @media (max-width: 767px) {
    &&.bw_search_box_compact .bw_search_compact_grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &&.bw_search_box_compact .bw_search_compact_actions {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 479px) {
    &&.bw_search_box_compact .bw_search_compact_grid {
      grid-template-columns: minmax(0, 1fr);
    }

    &&.bw_search_box_compact .bw_search_compact_actions {
      grid-column: 1;
    }
  }
`;

const LabelStyle = styled.span`
  opacity: 0.7;
`;

const EMPTY_ACTIONS = [];
const noop = () => {};

const FilterSearchBar = ({
  title = '',
  actions = EMPTY_ACTIONS,
  onSubmit = noop,
  onClear = noop,
  colSize = 3,
  expanded = false,
  disable,
  filterSearch,
  inlineActions = false,
  compact = false,
}) => {
  const [loadingLazy, setLoadingLazy] = useState(true);
  const methods = useFormContext();
  const [toggleSearch, setToggleSearch] = useState(true);
  const [searchAdvanced, setSearchAdvanced] = useState(expanded);

  useEffect(() => {
    setTimeout(() => {
      setLoadingLazy(false);
    }, [200]);
  }, []);

  const parseAction = actions.filter((p) => !p.hidden);
  const visibleActionLimit = compact
    ? Math.min(4, parseAction.length)
    : inlineActions
      ? (12 - colSize) / (filterSearch || colSize)
      : 12 / (filterSearch || colSize);
  const displayedActions = searchAdvanced ? parseAction : parseAction.slice(0, visibleActionLimit);

  const actionButtons = (
    <>
      <button
        id='filter-search-bar-trigger'
        type='submit'
        style={{
          display: 'none',
        }}></button>
      <button
        id='fitler_search_bar'
        className='bw_btn bw_btn_success bw_btn_icon_only'
        title='Tìm kiếm'
        aria-label='Tìm kiếm'
        type='button'
        onClick={methods.handleSubmit(onSubmit)}>
        <PortalIcon icon='fi fi-rr-filter' className='bw_btn_icon' style={{ lineHeight: 0 }} />
        <span className='bw_sr_only'>Tìm kiếm</span>
      </button>
      <button
        type='button'
        title='Làm mới'
        aria-label='Làm mới'
        onClick={() => {
          methods.reset({
            is_active: 1,
          });
          onClear({});
        }}
        className='bw_btn_outline bw_btn_icon_only'>
        <PortalIcon icon='fi fi-rr-refresh' className='bw_btn_icon' style={{ lineHeight: 0 }} />
        <span className='bw_sr_only'>Làm mới</span>
      </button>
    </>
  );

  return (
    <Wrapper className={`bw_search_box${compact ? ' bw_search_box_compact' : ''}`}>
      <h3 className='bw_title_search'>
        <span>{typeof title === 'string' ? normalizePortalText(title) : title}</span>
        {compact && parseAction.length > visibleActionLimit && !disable ? (
          <button
            type='button'
            className='bw_compact_advanced_toggle'
            onClick={() => setSearchAdvanced(!searchAdvanced)}>
            {!searchAdvanced ? 'Tìm nâng cao' : 'Ẩn bớt'}
          </button>
        ) : null}
        <span className='bw_close_search' onClick={() => setToggleSearch(!toggleSearch)}>
          <PortalIcon
            icon={toggleSearch ? 'fi fi-rr-angle-small-up' : 'fi fi-rr-angle-small-down'}
            style={{ cursor: 'pointer' }}
          />
        </span>
      </h3>
      {toggleSearch && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            methods.handleSubmit(onSubmit)(e);
          }}>
          <div
            className={`bw_row bw_mt_1 bw_mb_1 bw_search_fields_row${
              compact ? ' bw_search_compact_grid' : ''
            }`}
            style={compact ? { '--bw-filter-columns': Math.min(displayedActions.length, 4) } : undefined}>
            {displayedActions.map((props, index) => {
              const { title, isRequired, component } = props;
              return (
                <div key={`${props.title}${index}`} className={`bw_col_${colSize}`}>
                  <div className='bw_frm_box bw_h-100 '>
                    {loadingLazy ? (
                      <div className='loader__filter__searchbar' />
                    ) : (
                      <>
                        <LabelStyle>
                          {typeof title === 'string' ? normalizePortalText(title) : title}
                          {isRequired && <span className='bw_red'>*</span>}
                        </LabelStyle>
                        {component}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {!loadingLazy && inlineActions && (
              <div
                className={`bw_col_${colSize} bw_flex bw_justify_content_right bw_btn_group`}
                style={{ alignItems: 'flex-end', paddingBottom: 1 }}>
                {actionButtons}
              </div>
            )}
            {!loadingLazy && compact && (
              <div className='bw_search_compact_actions' role='group' aria-label='Thao tác bộ lọc'>
                {actionButtons}
              </div>
            )}
          </div>
          {!loadingLazy && !inlineActions && !compact && (
            <div className='bw_row bw_align_items_center bw_search_actions_row'>
              <div className='bw_col_6'>
                {parseAction.length > visibleActionLimit && (
                  <span onClick={() => setSearchAdvanced(!searchAdvanced)} className='bw_btn_more_search'>
                    {!disable && (!searchAdvanced ? 'Tìm nâng cao' : 'Ẩn bớt')}
                  </span>
                )}
              </div>
              <div className='bw_col_6 bw_flex bw_justify_content_right bw_btn_group'>
                {actionButtons}
              </div>
            </div>
          )}
        </form>
      )}
    </Wrapper>
  );
};

FilterSearchBar.propTypes = {
  /** Title of filter */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSubmit: PropTypes.func,
  onClear: PropTypes.func,
  inlineActions: PropTypes.bool,
  compact: PropTypes.bool,
};

export default FilterSearchBar;
